import { ref, computed } from 'vue'
import { uploadSupportAttachment } from '@/api/support'
import type { AttachmentUpload } from '@/types/support'

export function useSupportAttachments(organisationId: number) {
  const uploads = ref<AttachmentUpload[]>([])

  const attachmentIds = computed<number[]>(() =>
    uploads.value
      .filter((u) => u.status === 'done' && u.attachmentId !== undefined)
      .map((u) => u.attachmentId as number),
  )

  async function upload(file: File): Promise<void> {
    const tempId = `${Date.now()}-${Math.random().toString(36).slice(2)}`
    const entry: AttachmentUpload = { tempId, file, status: 'uploading' }
    uploads.value = [...uploads.value, entry]

    try {
      const attachment = await uploadSupportAttachment(organisationId, file)
      uploads.value = uploads.value.map((u) =>
        u.tempId === tempId ? { ...u, status: 'done', attachmentId: attachment.id } : u,
      )
    } catch {
      uploads.value = uploads.value.map((u) =>
        u.tempId === tempId ? { ...u, status: 'error' } : u,
      )
    }
  }

  async function retry(tempId: string): Promise<void> {
    const entry = uploads.value.find((u) => u.tempId === tempId)
    if (!entry) return
    uploads.value = uploads.value.map((u) =>
      u.tempId === tempId ? { ...u, status: 'uploading' } : u,
    )
    try {
      const attachment = await uploadSupportAttachment(organisationId, entry.file)
      uploads.value = uploads.value.map((u) =>
        u.tempId === tempId ? { ...u, status: 'done', attachmentId: attachment.id } : u,
      )
    } catch {
      uploads.value = uploads.value.map((u) =>
        u.tempId === tempId ? { ...u, status: 'error' } : u,
      )
    }
  }

  function remove(tempId: string): void {
    uploads.value = uploads.value.filter((u) => u.tempId !== tempId)
  }

  function reset(): void {
    uploads.value = []
  }

  return { uploads, attachmentIds, upload, retry, remove, reset }
}
